import type { ProductPaymentSettings } from '@/services';
import { Badge, Button, ConfirmActionModal, WarningNotice } from '@/shared/ui';
import { Check, Eye, EyeOff, Pencil, Plus } from 'lucide-react';
import { usePaymentMethodsCatalog } from '../hooks/use-payment-methods-catalog.hook';
import { CATALOG_SOURCE_LABELS } from '../utils/payment-settings-labels.util';
import { PaymentMethodFormModal } from './payment-method-form-modal.component';
import { SettingsChangesTable } from './settings-changes-table.component';
import { SettingsSection } from './settings-section.component';

type PaymentMethodsCatalogTabProps = {
  settings: ProductPaymentSettings;
  canEdit: boolean;
};

/** Aba "Catálogo de formas": as formas do produto, com edição e troca de status. */
export function PaymentMethodsCatalogTab({
  settings,
  canEdit,
}: PaymentMethodsCatalogTabProps) {
  const {
    rows,
    isEditable,
    catalogEditable,
    editor,
    openCreate,
    openEdit,
    closeEditor,
    pendingStatus,
    statusError,
    isSavingStatus,
    requestStatusToggle,
    closeStatus,
    confirmStatus,
  } = usePaymentMethodsCatalog(settings, canEdit);

  const isDeactivating = pendingStatus?.method.status === 'ACTIVE';

  return (
    <>
      {editor && (
        <PaymentMethodFormModal
          key={editor.mode === 'edit' ? editor.method.code : 'new'}
          productCode={settings.productCode}
          method={editor.mode === 'edit' ? editor.method : undefined}
          onClose={closeEditor}
        />
      )}

      <ConfirmActionModal
        isOpen={Boolean(pendingStatus)}
        onClose={closeStatus}
        onConfirm={() => void confirmStatus()}
        isLoading={isSavingStatus}
        errorMessage={statusError}
        tone={isDeactivating ? 'danger' : 'primary'}
        title={
          isDeactivating
            ? 'Desativar forma de pagamento'
            : 'Reativar forma de pagamento'
        }
        confirmLabel={isDeactivating ? 'Desativar' : 'Reativar'}
        description={
          isDeactivating
            ? 'A forma sai do combo da proposta. As apólices já emitidas com ela não mudam.'
            : 'A forma volta a aparecer no combo da proposta.'
        }
      >
        {pendingStatus && (
          <SettingsChangesTable changes={pendingStatus.changes} />
        )}
      </ConfirmActionModal>

      <SettingsSection
        title='Catálogo de formas do produto'
        description='Fonte única das formas do produto: o código vai para a apólice e para o I4Pro. Uma forma nunca é apagada, só desativada.'
        aside={
          isEditable ? (
            <Button
              type='button'
              variant='secondary'
              size='md'
              onClick={openCreate}
            >
              <Plus aria-hidden='true' className='h-4 w-4' />
              Adicionar forma
            </Button>
          ) : undefined
        }
      >
        {!catalogEditable && (
          <WarningNotice>
            Neste ambiente o catálogo ainda é lido do{' '}
            <strong>{CATALOG_SOURCE_LABELS[settings.catalogSource]}</strong> e
            fica somente leitura. A edição é liberada depois da virada para o
            DynamoDB.
          </WarningNotice>
        )}
        {settings.catalogInheritsFrom && (
          <p className='text-sm text-alba-gray-600'>
            Este produto também usa as formas do catálogo do{' '}
            <strong>{settings.catalogInheritsFrom}</strong>.
          </p>
        )}

        {rows.length === 0 ? (
          <p className='rounded-lg bg-alba-gray-200/50 p-4 text-center text-sm text-alba-gray-600'>
            Nenhuma forma cadastrada neste produto.
          </p>
        ) : (
          <div className='overflow-x-auto rounded-lg border border-alba-gray-200'>
            <table className='w-full min-w-[640px] text-left text-sm'>
              <thead className='bg-alba-gray-200/50 text-xs text-alba-gray-600'>
                <tr>
                  <th scope='col' className='px-3 py-2 font-medium'>
                    Código
                  </th>
                  <th scope='col' className='px-3 py-2 font-medium'>
                    Descrição
                  </th>
                  <th scope='col' className='px-3 py-2 font-medium'>
                    Uso no gateway
                  </th>
                  <th scope='col' className='px-3 py-2 font-medium'>
                    Banco
                  </th>
                  <th scope='col' className='px-3 py-2 font-medium'>
                    Status
                  </th>
                  <th scope='col' className='px-3 py-2 font-medium'>
                    No combo
                  </th>
                  {isEditable && (
                    <th scope='col' className='px-3 py-2 font-medium'>
                      <span className='sr-only'>Ações</span>
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className='divide-y divide-alba-gray-200'>
                {rows.map(row => (
                  <tr
                    key={row.method.code}
                    className={row.isActive ? undefined : 'text-alba-gray-500'}
                  >
                    <td className='whitespace-nowrap px-3 py-2 font-medium text-alba-black-500'>
                      {row.method.code}
                    </td>
                    <td className='px-3 py-2'>{row.method.description}</td>
                    <td className='px-3 py-2'>
                      {row.gatewayLabel ? (
                        <Badge tone='blue'>{row.gatewayLabel}</Badge>
                      ) : (
                        row.billingLabel
                      )}
                    </td>
                    <td className='px-3 py-2'>{row.bankLabel}</td>
                    <td className='px-3 py-2'>
                      <Badge tone={row.isActive ? 'success' : 'gray'}>
                        {row.isActive ? 'Ativa' : 'Inativa'}
                      </Badge>
                    </td>
                    <td className='px-3 py-2'>
                      {row.isShownInCombo ? (
                        <Check
                          aria-label='Aparece no combo'
                          className='h-4 w-4 text-alba-success-green-600'
                        />
                      ) : (
                        <span aria-label='Não aparece no combo'>—</span>
                      )}
                    </td>
                    {isEditable && (
                      <td className='whitespace-nowrap px-3 py-2 text-right'>
                        <Button
                          type='button'
                          variant='ghost'
                          size='sm'
                          aria-label={`Editar a forma ${row.method.code}`}
                          onClick={() => openEdit(row.method)}
                        >
                          <Pencil aria-hidden='true' className='h-4 w-4' />
                        </Button>
                        <Button
                          type='button'
                          variant='ghost'
                          size='sm'
                          aria-label={`${row.isActive ? 'Desativar' : 'Reativar'} a forma ${row.method.code}`}
                          onClick={() => requestStatusToggle(row.method)}
                        >
                          {row.isActive ? (
                            <EyeOff aria-hidden='true' className='h-4 w-4' />
                          ) : (
                            <Eye aria-hidden='true' className='h-4 w-4' />
                          )}
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className='text-xs text-alba-gray-500'>
          &quot;No combo&quot; mostra se o corretor vê a forma no step Pagamento
          da proposta com a cobrança salva hoje. As formas do gateway são
          cobradas por link; as legadas, pelo I4Pro. Para tirar uma forma do
          combo, desative-a.
        </p>
      </SettingsSection>
    </>
  );
}
